'use client';

import classNames from 'classnames';
import { IImage } from '@sapientpro/sapientpro-data-models';
import { useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import styles from './Case.module.scss';
import Img from '../../../components/Img';
import clientDeviceSize, { ScreenSize } from '../../../store/deviceScreenSize';
import useInViewport from '../../../useInViewport';

interface Classes {
  case?: string;
  caseInfo?: string;
  caseMedia?: string;
  caseMediaImage?: string;
  caseMediaVideo?: string;
}

interface CaseData {
  id: number
  title: string;
  description: string;
}

interface CaseImageProps extends CaseData {
  image: IImage;
  video?: never;
}

interface CaseVideoProps extends CaseData {
  video?: IImage;
  image?: IImage;
}

type CaseDataProps = CaseImageProps | CaseVideoProps;

interface CaseProps {
  caseData: CaseDataProps;
  classes?: Classes;
}

const Case = ({
  caseData,
  classes,
}: CaseProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const caseRef = useRef<HTMLDivElement>(null);
  const isVisible = useInViewport(caseRef);

  const deviceSize = useAtomValue<ScreenSize>(clientDeviceSize);

  const isMobile = (deviceSize <= ScreenSize.TABLET_PORTRAIT);

  const playVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const pauseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  useEffect(
    () => {
      if (!isMobile) {
        return;
      }

      isVisible ? playVideo() : pauseVideo();
    },
    [isVisible],
  );

  const videoTypeSuffix = caseData.video ? caseData?.video?.ext?.slice(1) : '';

  return (
    <div
      ref={caseRef}
      {...!isMobile ? {
        onMouseEnter: playVideo,
        onMouseLeave: pauseVideo,
      } : {}}
      className={classNames(styles.case, classes?.case)}
    >
      <div className={classNames(styles.caseInfo, classes?.caseInfo)}>
        <h3 className={classNames(styles.caseInfo__title, 'header3')}>{caseData.title}</h3>
        <div
          className={styles.caseInfo__description}
          dangerouslySetInnerHTML={{ __html: caseData.description }}
        />
      </div>
      <div className={classNames(styles.caseMedia, classes?.caseMedia)}>
        {caseData.image ? (
          <Img
            src={caseData.image.url}
            alt={caseData.image.alternativeText || 'service'}
            className={classNames(styles.caseMedia__image, classes?.caseMediaImage)}
            priority
            withPreloader
          />
        )
          : caseData.video && (
          <video
            loop
            muted
            playsInline
            ref={videoRef}
            className={classNames(styles.caseMedia__video, classes?.caseMediaVideo)}
            onClick={(e) => e.preventDefault()}
          >
            <source
              src={caseData.video.url}
              type={`video/${videoTypeSuffix}`}
            />
            Your browser does not support MP4 video.
          </video>
          )}
      </div>
    </div>
  );
};

export default Case;
